import { TeamResource } from 'dy-frontend-http-repository/lib/modules/Team/resources';
import React, { useEffect, useState } from 'react';

import { usePageTitle } from '@app/hooks';
import { Callout, Intent, Spinner, Tab, Tabs } from '@blueprintjs/core';
import FixedWidthPageContainer from '@components/FixedWidthPageContainer';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import { LocalStorage } from '@data/enums';

import MonthlyOccupancy from './components/MonthlyOccupancy';
import QuarterlyOccupancy from './components/QuarterlyOccupancy';
import WeeklyOccupancy from './components/WeeklyOccupancy';
import { fetchActiveTeams } from './data/api';
import { TeamHealthView, parseTeamHealthView } from './utils/teamHealth';

const readStoredView = (): TeamHealthView => {
    try {
        return parseTeamHealthView(localStorage.getItem(LocalStorage.TEAM_HEALTH_VIEW));
    } catch {
        return parseTeamHealthView(null);
    }
};

const storeView = (view: TeamHealthView) => {
    try {
        localStorage.setItem(LocalStorage.TEAM_HEALTH_VIEW, view);
    } catch {
        // Storage unavailable: the view simply is not remembered
    }
};

const TeamHealth: React.FC = () => {
    usePageTitle('Occupancy');

    const [view, setView] = useState<TeamHealthView>(readStoredView);
    const [teams, setTeams] = useState<TeamResource[] | null>(null);
    const [isTeamsError, setIsTeamsError] = useState(false);

    useEffect(() => {
        let isCancelled = false;
        fetchActiveTeams()
            .then((items) => !isCancelled && setTeams(items))
            .catch((e) => {
                console.error(e);
                if (!isCancelled) {
                    setIsTeamsError(true);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, []);

    const renderContent = () => {
        if (isTeamsError) {
            return <Callout intent={Intent.DANGER}>Teams could not be loaded.</Callout>;
        }

        if (!teams) {
            return (
                <Flex justify="center">
                    <Spinner />
                </Flex>
            );
        }

        if (view === 'quarterly') {
            return <QuarterlyOccupancy teams={teams} />;
        }

        return view === 'monthly' ? <MonthlyOccupancy teams={teams} /> : <WeeklyOccupancy teams={teams} />;
    };

    return (
        <FixedWidthPageContainer>
            <Heading type="h3" className="mb-2">
                Occupancy
            </Heading>
            <Tabs
                id="team-health-view"
                className="mb-2"
                large
                selectedTabId={view}
                onChange={(tabId) => {
                    const nextView = parseTeamHealthView(String(tabId));
                    setView(nextView);
                    storeView(nextView);
                }}
            >
                <Tab id="monthly" title="Monthly" />
                <Tab id="weekly" title="Weekly" />
                <Tab id="quarterly" title="Quarterly" />
            </Tabs>
            {renderContent()}
        </FixedWidthPageContainer>
    );
};

export default TeamHealth;
